import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type RelationshipMapResult } from '@whinerp/client';
import { isMessageKey } from '@whinerp/contracts/i18n';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The relationship map (G-035), on a company and on a deal.
 *
 * **Drawn as an indented org chart, not a node-and-arrow diagram.** A graph
 * of floating boxes cannot be followed with a keyboard or read aloud, and at
 * phone width it becomes a tangle. A nested list IS the org chart, reads in
 * the right order to a screen reader, and keeps the dotted and influence lines
 * in words under the person they belong to.
 *
 * **A withheld person is shown as withheld.** Their place in the chart stays,
 * so the chart does not quietly claim that somebody reports to nobody.
 *
 * On a deal, each person's part in it is shown beside them, and the insights
 * - a champion who reports up to a blocker, a deal with no decision-maker -
 * come first, because they are the reason to open this on a deal at all.
 */

type RelationshipMap = RelationshipMapResult['map'];
type Person = RelationshipMap['people'][number];
type Kind = RelationshipMap['lines'][number]['kind'];

export function RelationshipMapPanel({
  accountId,
  dealId,
}: {
  accountId: string;
  dealId?: string | null;
}): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-028');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ from: string; to: string; kind: Kind }>({ from: '', to: '', kind: 'reports-to' });

  const key = ['relationship-map', accountId, dealId ?? null];
  const map = useQuery<RelationshipMapResult, ApiError>({
    queryKey: key,
    queryFn: () =>
      api.relationshipMap({ path: { accountId }, query: dealId ? { dealId } : {} }),
    enabled: enabled.enabled,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['relationship-map', accountId] });

  const add = useMutation({
    mutationFn: () =>
      api.addRelationship({
        path: { accountId },
        body: { fromContactId: draft.from, toContactId: draft.to, kind: draft.kind },
      }),
    onSuccess: () => {
      setAdding(false);
      setDraft({ from: '', to: '', kind: 'reports-to' });
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (relationshipId: string) =>
      api.removeRelationship({ path: { accountId, relationshipId } }),
    onSuccess: refresh,
  });

  if (!enabled.enabled || map.isPending) return null;
  if (map.isError) {
    return (
      <Panel data-region="relationship-map">
        <p className="text-dense text-danger-text">{errorText(map.error)}</p>
      </Panel>
    );
  }

  const data = map.data.map;
  const byId = new Map(data.people.map((person) => [person.contactId, person]));
  const reportsTo = data.lines.filter((line) => line.kind === 'reports-to');
  const childrenOf = (managerId: string) =>
    reportsTo.filter((line) => line.toContactId === managerId).map((line) => line.fromContactId);

  // People in this company, by name, for the add form. Withheld people and
  // people elsewhere cannot be chosen here.
  const pickable = data.people
    .filter((person) => !person.withheld && person.elsewhere === null)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  const nameOf = (person: Person | undefined): string =>
    !person ? t('relmap.unknown') : person.withheld ? t('relmap.withheld') : person.name ?? t('relmap.unnamed');

  const renderPerson = (contactId: string, seen: Set<string>): JSX.Element => {
    const person = byId.get(contactId);
    const next = new Set(seen).add(contactId);
    const side = data.lines.filter((line) => line.kind !== 'reports-to' && line.fromContactId === contactId);
    const reports = childrenOf(contactId).filter((id) => !seen.has(id));
    const managerLine = reportsTo.find((line) => line.fromContactId === contactId);

    return (
      <li key={contactId} data-relmap-person={contactId} data-relmap-withheld={person?.withheld ? 'true' : undefined}>
        <div className="flex flex-wrap items-baseline gap-2 py-1">
          <span className={person?.withheld ? 'text-ink-meta italic' : 'text-ink'}>{nameOf(person)}</span>
          {person?.title ? <span className="text-meta text-ink-meta">{person.title}</span> : null}
          {person?.elsewhere ? <span className="text-meta text-ink-meta">· {person.elsewhere}</span> : null}
          {person?.dealRole ? (
            <span className="rounded-chip bg-sunken px-2 text-meta text-ink-600" data-relmap-role={person.dealRole}>
              {/* Checked against the catalogue: a role added to deals later
                  shows as itself rather than as a missing key. */}
              {(() => {
                const key = `relmap.role.${person.dealRole}`;
                return isMessageKey(key) ? t(key) : person.dealRole;
              })()}
            </span>
          ) : null}
          {managerLine ? (
            <button
              type="button"
              data-relmap-remove-manager={contactId}
              className="text-meta text-ink-meta underline-offset-2 hover:underline"
              onClick={() => remove.mutate(managerLine.relationshipId)}
            >
              {t('relmap.removeManager')}
            </button>
          ) : null}
        </div>
        {side.length > 0 ? (
          <ul className="ms-4 flex flex-col gap-0.5 text-meta text-ink-meta">
            {side.map((line) => (
              <li key={line.relationshipId} data-relmap-line={line.kind} className="flex flex-wrap items-center gap-2">
                <span>
                  {t(line.kind === 'dotted-line' ? 'relmap.dotted' : 'relmap.influences', {
                    values: { name: nameOf(byId.get(line.toContactId)) },
                  })}
                </span>
                {line.note ? <span>— {line.note}</span> : null}
                <button
                  type="button"
                  className="text-meta text-ink-meta underline-offset-2 hover:underline"
                  onClick={() => remove.mutate(line.relationshipId)}
                  aria-label={t('relmap.removeLine')}
                >
                  {t('relmap.remove')}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {reports.length > 0 ? (
          <ul className="ms-4 border-s border-line ps-3">{reports.map((id) => renderPerson(id, next))}</ul>
        ) : null}
      </li>
    );
  };

  // The tops of the chart: people here with no manager, plus any manager the
  // chart reaches who is not in this company's own list (withheld, typically),
  // so nobody under them drops out of the drawing.
  const inChart = new Set<string>();
  const walk = (id: string) => {
    if (inChart.has(id)) return;
    inChart.add(id);
    childrenOf(id).forEach(walk);
  };
  data.roots.forEach(walk);
  const orphanTops = [...new Set(reportsTo.map((line) => line.toContactId))].filter(
    (id) => !inChart.has(id) && !reportsTo.some((line) => line.fromContactId === id),
  );
  const tops = [...data.roots, ...orphanTops];

  return (
    <Panel data-region="relationship-map">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <SectionLabel>{t('relmap.title')}</SectionLabel>
          {!adding && pickable.length > 1 ? (
            <Button intent="secondary" size="small" data-relmap-add onClick={() => setAdding(true)}>
              {t('relmap.add')}
            </Button>
          ) : null}
        </div>

        {/* First, on a deal: they are why anybody opens the map there. */}
        {data.insights.length > 0 ? (
          <ul className="flex flex-col gap-1" data-relmap-insights>
            {data.insights.map((insight) => (
              <li key={`${insight.kind}-${insight.contactIds.join('-')}`} data-relmap-insight={insight.kind}
                className="text-dense text-warning-text">
                {insight.message}
              </li>
            ))}
          </ul>
        ) : null}

        {add.error ?? remove.error ? (
          <p className="text-dense text-danger-text" data-relmap-error>
            {errorText((add.error ?? remove.error) as ApiError)}
          </p>
        ) : null}

        {data.people.length === 0 ? (
          <p className="text-dense text-ink-meta" data-relmap-empty>{t('relmap.empty')}</p>
        ) : (
          <ul className="flex flex-col text-dense" data-relmap-chart>
            {tops.map((id) => renderPerson(id, new Set()))}
          </ul>
        )}

        {adding ? (
          <form
            data-relmap-form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              add.mutate();
            }}
          >
            {(['from', 'kind', 'to'] as const).map((field) =>
              field === 'kind' ? (
                <label key={field} className="flex flex-col gap-1 text-meta text-ink-meta">
                  {t('relmap.kind')}
                  <select
                    data-relmap-field="kind"
                    value={draft.kind}
                    onChange={(event) => setDraft({ ...draft, kind: event.target.value as Kind })}
                    className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                  >
                    {(['reports-to', 'dotted-line', 'influences'] as const).map((kind) => (
                      <option key={kind} value={kind}>{t(`relmap.kind.${kind}`)}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <label key={field} className="flex flex-col gap-1 text-meta text-ink-meta">
                  {t(field === 'from' ? 'relmap.from' : 'relmap.to')}
                  <select
                    data-relmap-field={field}
                    value={draft[field]}
                    required
                    onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                    className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                  >
                    <option value="">{t('relmap.choose')}</option>
                    {pickable.map((person) => (
                      <option key={person.contactId} value={person.contactId}>{person.name}</option>
                    ))}
                  </select>
                </label>
              ),
            )}
            <Button intent="primary" size="small" type="submit" data-relmap-save disabled={add.isPending}>
              {t('relmap.save')}
            </Button>
            <Button intent="secondary" size="small" type="button" onClick={() => setAdding(false)}>
              {t('relmap.cancel')}
            </Button>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}
